/* eslint-disable react-native-enforce-testid/testid-missing */
import { useNavigation } from "@react-navigation/native";
import React, { memo, useCallback, useEffect, useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Image,
  ActivityIndicator,
  Linking,
  TouchableNativeFeedback,
  SectionList,
  Platform,
  Alert,
  BackHandler,
} from "react-native";

import debounce from "lodash/debounce";

import dayjs from "dayjs";

import t from "@constants/localization";
import colors from "@constants/color";
import analytics from "@services/analytics";
import {
  MY_DELIVERY_EVENTS,
  RRD_EVENTS,
  SEARCH_EVENTS,
} from "@constants/analyticsEventsCT";
import CallIcon from "@assets/svg/newCall";
import Tick from "@assets/svg/MyDeliveries/Tick";
import {
  getAllWalletMetaData,
  getWalletSummary,
  markRefunded,
  unMarkRefunded,
} from "@services/api";
import CustomText from "@components/CustomText";
import Dot from "@assets/svg/Dot";
import Location from "@assets/images/LocationG.png";
import ChevronRight from "@assets/svg/ChevronRight";
import Error from "@assets/svg/Error";
import Carousel from "@components/RRD/Carousel";
import Button from "@components/RRD/Button";
import ToastMessage from "@components/Toast";
import { useDispatch, useSelector } from "react-redux";
import RRDStatus from "@assets/svg/RRDStatus";
import { actions as walletActions } from "@reducers/oldWalletReducer";
import { actions as rrdActions } from "@reducers/rrdReducer";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import HeaderWithSearch from "@components/SearchHeader/HeaderWithSearch";
import SearchIcon from "@assets/svg/SearchIcon";
import { filterData } from "@components/SearchHeader/utils";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import CustomModal from "../OrdersList/CustomModal";

const PATH = "screens/RRD/Refund";

export const Text = (props) => {
  const { children, ...rest } = props;
  return (
    <CustomText newfont {...rest}>
      {children}
    </CustomText>
  );
};

const onPressCall = (phone, userId) => {
  handleCallButtonPress(phone, MY_DELIVERY_EVENTS.DB_CALLED, {
    user_id: userId,
  });
};

export const formatDate = (date) => {
  return dayjs(date).format("dddd, DD MMMM YYYY, h:mmA");
};

const Refunds = ({ route }) => {
  const navigation = useNavigation();
  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [actionData, setActionData] = useState();

  const dispatch = useDispatch();

  useEffect(() => {
    const backHandler = () => {
      if (isSearchEnabled) {
        onSearchInputChange("");
        setIsSearchEnabled((prev) => !prev);
        return true;
      }
      return false;
    };

    const subscription = BackHandler.addEventListener("hardwareBackPress", backHandler);

    return () => {
      subscription.remove();
    };
  }, [isSearchEnabled, onSearchInputChange]);

  const { overviewData, refundsData } = useSelector((state) => state.rrd);
  useEffect(() => {
    setListData([
      { key: "delayedCustomers", data: refundsData.delayedCustomers },
      { key: "customers", data: refundsData.customers },
      { key: "completedCustomers", data: refundsData.completedCustomers },
    ]);
    fetchWalletData();
  }, [
    overviewData,
    fetchWalletData,
    refundsData.delayedCustomers,
    refundsData.customers,
    refundsData.completedCustomers,
  ]);

  const fetchWalletData = useCallback(() => {
    getAllWalletMetaData()
      .then(({ data }) => {
        const {
          default: { toDate, fromDate },
        } = data.filters;
        dispatch(walletActions.setWalletMetaData(data));
        return getWalletSummary({ to: toDate, from: fromDate });
      })
      .then(({ data }) => {
        dispatch(walletActions.setWalletSummary(data));
        dispatch(
          walletActions.setCurrentFilter("custom", {
            fromDate: dayjs().subtract(10, "day").format("YYYY-MM-DD") || null,
            toDate: dayjs().format("YYYY-MM-DD") || null,
          }),
        );
        setIsLoading(false);
      })
      .catch((err) => {
        console.log("eror from wallet fetch", err);
      });
  }, [dispatch]);

  const renderSection = ({ section: { key, data } }) => {
    if (data?.length === 0) return null;
    return (
      <View style={styles.sectionHeader}>
        <RRDStatus status={key} />
        <Text newfont variant="subtitle_16" style={styles.sectionHeaderText}>
          {t(`${PATH}/${key}`, { count: refundsData?.tasksCount[key] })}
        </Text>
      </View>
    );
  };

  const renderItem = ({ item, section }) => (
    <Card item={item} section={section} />
  );

  const keyExtractor = (item, index) => `${item.user_id}-${index}`;

  const onCardPress = (data, item) => {
    analytics.trackEvent(RRD_EVENTS.MARKING_CARD_CLICKED, {
      card_type: "refund",
      user_id: item?.user_id,
      user_name: item?.user_name,
      return_req_id: data?.return_req_id,
      ticket_raised: data?.is_cx_complaint_raised,
      delay: data?.delay_count_in_hours,
    });
  };

  const openMaps = (latitude, longitude) => {
    try {
      Linking.openURL(
        Platform.OS === "ios"
          ? `https://maps.apple.com/maps?daddr=${latitude},${longitude}`
          : `https://maps.google.com/maps?q=loc:${latitude},${longitude}`,
      );
    } catch (error) {
      Alert.alert(t(`${PATH}/mapsAlert`));
    }
  };

  const handleBtnEvents = (action, data, item) => {
    const {
      return_req_id,
      order_id,
      delay_count_in_hours,
      is_cx_complaint_raised,
    } = data;
    const { user_id, user_name, distance } = item;
    analytics.trackEvent(RRD_EVENTS.MARKING_PAGE_CTA_CLICKED, {
      return_req_id,
      order_id,
      delay_count_in_hours,
      is_cx_complaint_raised,
      user_id,
      user_name,
      distance,
      Marking_type: action,
      screen: "Mark delivery/return",
      card_type: action.includes("Delivery") ? "delivery" : "return",
    });
  };

  const handleOTPFlow = async (response, action, data) => {
    if (response?.data?.error === "OTP_VERIFICATION_FAILED") {
      navigation.navigate("OTPVerification", {
        action,
        id: data?.return_req_id ?? data?.order_id,
        data,
        otpLength: response?.data?.otpLength || 4,
        otpText: response?.data?.otpText || "",
      });
    } else if (response.status === 200) {
      dispatch(rrdActions.fetchRRDInfo());
      ToastMessage("success", response?.data?.toastMessage);
    }
    setShowModal(false);
    setActionData({});
  };

  const onButtonPress = async (action, data, item) => {
    handleBtnEvents(action, data, item);
    if (!showModal && overviewData?.showModalsFor.includes(action)) {
      setActionData({ action, data, item });
      return setShowModal(true);
    }
    let response;
    if (action === "confirmRefund") {
      response = await markRefunded(data?.return_req_id);
    } else {
      response = await unMarkRefunded(data?.return_req_id);
    }
    handleOTPFlow(response, action, data);
  };

  const Card = memo(({ item, section }) => {
    return (
      <View style={styles.listView}>
        <View style={styles.cardHeaderContainer}>
          <View style={styles.view1}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text newfont variant="body_text_2" style={styles.name}>
                {item?.user_name}
              </Text>
              <Dot />
              <Text
                newfont
                variant="body_text_3"
                style={{ paddingHorizontal: 2, color: colors.lightBlack }}
              >
                {item?.distance}
              </Text>
              <TouchableOpacity
                onPress={() => openMaps(item?.cx_lat, item?.cx_lng)}
              >
                <Image style={{ height: 14, width: 14 }} source={Location} />
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              onPress={() => onPressCall(item?.phone_number, item?.user_id)}
            >
              <CallIcon strokeColor="#2195F2" width={17} height={17} />
            </TouchableOpacity>
          </View>
          <Text style={styles.address}>{item?.address}</Text>
        </View>
        <View>
          {item?.refunds?.map((data, idx, arr) => (
            <TouchableNativeFeedback
              key={`${data.count}-${data.amount}`}
              onPress={() => onCardPress(data, item)}
            >
              <View
                style={{
                  backgroundColor:
                    idx % 2 === 0 && arr.length > 1 ? "#F5F5F5" : "default",
                }}
              >
                <DelayedTag
                  type="refund"
                  {...{ data, section: section.key, overviewData }}
                />
                <View style={styles.view2}>
                  <View>
                    <Text newfont variant="body_text_3" style={styles.text2}>
                      {t(`${PATH}/return`)}
                    </Text>
                    <Text newfont variant="body_text_3" style={styles.text3}>
                      {data?.count}
                    </Text>
                  </View>
                  <View style={styles.separator} />
                  <View style={styles.cardItemSection}>
                    <View>
                      <Text newfont variant="body_text_3" style={styles.text2}>
                        {t(`${PATH}/refund`)}
                      </Text>
                      <Text newfont variant="body_text_3" style={styles.text3}>
                        {t(`${PATH}/rs`)}
                        {data?.amount}
                      </Text>
                    </View>
                    <View style={styles.onlineTag}>
                      <Tick visible={true} />
                      <Text newfont style={styles.online}>
                        {t(`${PATH}/received`)}
                      </Text>
                    </View>
                  </View>
                </View>
                <View style={styles.horizontalSeparator} />
                {data?.return_req_items?.map(ItemCard)}
                <View style={styles.bottomButtonContainer}>
                  {data?.status === "MARKED_REFUND_TO_CX" ? (
                    <View style={styles.unmarkContainer}>
                      <View style={styles.tickContainer}>
                        <Tick visible={true} fill="white" />
                      </View>
                      <View style={{ flex: 1 }}>
                        <View style={styles.unmarkTextContainer}>
                          <Text newfont variant="body_text_2">
                            {t(`${PATH}/refunded`)}
                          </Text>
                          {data?.can_unmark_refund ? (
                            <TouchableOpacity
                              onPress={() =>
                                onButtonPress("unmarkPickup", data, item)
                              }
                            >
                              <Text
                                variant="caption_1"
                                style={styles.unmarkBtn}
                              >
                                {t(`${PATH}/unmark`)}
                              </Text>
                            </TouchableOpacity>
                          ) : null}
                        </View>
                        <Text
                          newfont
                          variant="caption_2"
                          style={styles.unmarkDate}
                        >
                          {formatDate(data?.refunded_to_cx_at)}
                        </Text>
                      </View>
                    </View>
                  ) : (
                    <View style={styles.buttonContainer}>
                      <TouchableOpacity
                        onPress={() => viewRefund(data)}
                        style={styles.viewRefundBtn}
                      >
                        <Text
                          newfont
                          style={{ color: colors.lightBlack }}
                          variant="body_text_4"
                        >
                          {t(`${PATH}/viewRefund`)}
                        </Text>
                        <View style={styles.icon}>
                          <ChevronRight pathFill={colors.lightBlack} />
                        </View>
                      </TouchableOpacity>
                      <Button
                        title={t(`${PATH}/markRefund`)}
                        btnColor={colors.plain_green}
                        onPress={() =>
                          onButtonPress("confirmRefund", data, item)
                        }
                      />
                    </View>
                  )}
                </View>
              </View>
            </TouchableNativeFeedback>
          ))}
        </View>
      </View>
    );
  });

  const viewRefund = (data) => {
    analytics.trackEvent(RRD_EVENTS.VIEW_REFUND_IN_GULLAK, {
      return_request_id: data?.return_req_id,
    });
    navigation.navigate("Passbook", {
      wt_ids: data?.wt_id,
    });
  };

  const onSearchInputChange = debounce((text) => {
    const extraFilters = [
      // eslint-disable-next-line no-shadow
      (item, text) =>
        item?.refunds?.length > 0 &&
        item?.refunds?.some((refundItem) =>
          refundItem?.order_id
            ?.toLowerCase()
            ?.startsWith(text?.trim().toLowerCase()),
        ),
    ];

    setListData([
      {
        key: "delayedCustomers",
        data: filterData(text, refundsData?.delayedCustomers, extraFilters),
      },
      {
        key: "customers",
        data: filterData(text, refundsData?.customers, extraFilters),
      },
      {
        key: "completedCustomers",
        data: filterData(text, refundsData?.completedCustomers, extraFilters),
      },
    ]);
  }, 500);

  const onPressCrossIcon = () => {
    onSearchInputChange("");
    analytics.trackEvent(SEARCH_EVENTS.SEARCH_CROSS_CLICKED, {
      screen: "Refunds",
    });
  };
  const onPressSearchIcon = () => {
    setIsSearchEnabled(!isSearchEnabled);
    analytics.trackEvent(SEARCH_EVENTS.SEARCH_CLICKED, {
      screen: "Refunds",
    });
  };
  const goingBackHandler = () => {
    onSearchInputChange("");
    setIsSearchEnabled(false);
  };

  const checkListDataIsEmpty = () => {
    if (listData) {
      return listData?.every((item) => item?.data?.length === 0);
    }
    return true;
  };

  return (
    <View style={styles.container}>
      <HeaderWithSearch
        headertitle={
          !isSearchEnabled
            ? t(`${PATH}/heading`, {
                count:
                  (overviewData?.refunds_overview?.Total ?? 0) +
                  (overviewData?.refunds_overview?.Completed ?? 0),
              })
            : t(`SearchOrder/title`)
        }
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        searchIcon={<SearchIcon height={20} width={20} />}
        isSearchEnabled={isSearchEnabled}
        searchPlaceholder={t(`SearchOrder/placeholder`)}
        onPressCrossIcon={onPressCrossIcon}
        goingBackHandler={isSearchEnabled ? goingBackHandler : null}
        onSearchInputChange={onSearchInputChange}
        onPressSearchIcon={onPressSearchIcon}
      />
      <CustomModal
        heading={refundsData?.popups[actionData?.action]?.heading ?? ""}
        message={refundsData?.popups[actionData?.action]?.message ?? ""}
        buttons={refundsData?.popups[actionData?.action]?.buttons}
        visible={showModal}
        onPress={() =>
          onButtonPress(actionData?.action, actionData?.data, actionData?.item)
        }
        fontFamily={false}
        headingFontWeight="bold"
        headingFontSize={16}
        messageFontSize={14}
        setShowModal={() => setShowModal(false)}
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : checkListDataIsEmpty() ? (
        <EmptySearchList />
      ) : (
        <SectionList
          renderSectionHeader={renderSection}
          keyExtractor={keyExtractor}
          sections={listData}
          renderItem={renderItem}
          ListHeaderComponent={Carousel(refundsData?.carousel)}
          keyboardShouldPersistTaps="handled"
        />
      )}
    </View>
  );
};

export const ItemCard = ({ short_name, quantity, product_image, amount }) => {
  return (
    <View>
      <View key={`${short_name}-${quantity}`} style={styles.itemContainer}>
        <Image
          source={{ uri: product_image }}
          style={styles.itemImage}
          resizeMode="cover"
          alt="Product Image"
        />
        <View style={styles.itemDescContainer}>
          <Text newfont variant="body_text_3">
            {short_name}
          </Text>
          <View style={styles.descContainer}>
            <Text newfont variant="body_text_1">
              {t(`${PATH}/rs`)}
              <Text
                newfont
                variant="body_text_1"
                style={{ fontFamily: "NotoSansDevanagari-Bold" }}
              >
                {amount}
              </Text>
            </Text>
            <View style={styles.itemDescSeparator} />
            <Text newfont variant="body_text_1">
              Qty •{" "}
              <Text
                newfont
                variant="body_text_1"
                style={{ fontFamily: "NotoSansDevanagari-Bold" }}
              >
                {quantity}
              </Text>
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

export const DelayedTag = ({ data, type, section, overviewData }) => {
  if (
    data?.delay_count_in_hours === 0 ||
    section === "completedCustomers" ||
    data?.delivered_to_cx_at
  ) {
    return null;
  }
  return (
    <View>
      <View style={styles.delayedTag}>
        <Text newfont variant="caption_1" style={{ color: colors.white }}>
          {data?.delay_count_in_hours > 24
            ? t(`${PATH}/delayInDays`, {
                count: (data?.delay_count_in_hours / 24).toFixed(0),
              })
            : t(`${PATH}/delayInHours`, { count: data?.delay_count_in_hours })}
        </Text>
      </View>
      {data?.is_cx_complaint_raised && (
        <View style={styles.complaintRaisedContainer}>
          <Error />
          <Text
            newfont
            variant="body_text_3"
            style={styles.complaintRaisedText}
          >
            {overviewData?.complaintRaised[type]}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listView: {
    backgroundColor: colors.white,
    borderRadius: 10,
    elevation: 4,
    overflow: "hidden",
    marginHorizontal: 16,
    marginVertical: 8,
  },
  view1: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  sectionHeaderText: {
    marginHorizontal: 6,
  },
  view2: {
    flexDirection: "row",
    // marginTop: 12,
    alignItems: "center",
    paddingHorizontal: 16,
    marginVertical: 8,
  },
  text2: {
    color: "rgba(0, 0, 0, 0.56)",
  },
  text3: {
    fontFamily: "NotoSansDevanagari-Bold",
  },
  name: {
    fontSize: 14,
    lineHeight: 24,
    color: "black",
    fontWeight: "bold",
    paddingHorizontal: 2,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  onlineTag: {
    borderRadius: 20,
    borderWidth: 1,
    padding: 6,
    paddingVertical: 0.5,
    alignItems: "center",
    marginHorizontal: 8,
    borderColor: colors.lighterBlack,
    flexDirection: "row",
  },
  online: {
    color: colors.plain_green,
    fontFamily: "NotoSansDevanagari-Bold",
    fontSize: 10,
    lineHeight: 14,
  },
  address: {
    maxWidth: "85%",
    color: colors.lightBlack,
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-Regular",
  },
  separator: {
    backgroundColor: colors.lighterBlack,
    height: 24,
    width: 1,
    marginHorizontal: 24,
  },
  itemContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  itemImage: {
    height: 60,
    width: 60,
  },
  itemDescContainer: {
    paddingHorizontal: 16,
    width: "80%",
  },
  itemDescSeparator: {
    backgroundColor: colors.lightMediumBlack,
    height: 22,
    width: 1,
    marginHorizontal: 16,
  },
  descContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 2,
  },

  cardItemSection: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
  },
  cardHeaderContainer: {
    padding: 16,
  },
  icon: {
    height: 24,
    width: 24,
    padding: 6,
  },
  horizontalSeparator: {
    backgroundColor: "rgba(0,0,0,0.05)",
    height: 1,
    width: "120%",
    marginTop: 8,
  },
  delayedTag: {
    marginHorizontal: 16,
    marginVertical: 12,
    backgroundColor: colors.plain_red,
    borderRadius: 18,
    alignItems: "center",
    alignSelf: "flex-start",
    paddingHorizontal: 8,
  },
  complaintRaisedContainer: {
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 4,
    alignItems: "center",
  },
  complaintRaisedText: {
    color: colors.lightBlack,
    maxWidth: "80%",
    paddingHorizontal: 8,
  },
  unmarkContainer: { width: "100%", flexDirection: "row" },
  unmarkTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  unmarkBtn: { textDecorationLine: "underline", color: colors.lightBlack },
  unmarkDate: { color: colors.lightBlack },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  viewRefundBtn: {
    paddingRight: 10,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
  bottomButtonContainer: {
    marginVertical: 16,
    paddingHorizontal: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  tickContainer: {
    backgroundColor: "#00AB59",
    height: 20,
    width: 20,
    borderRadius: 12,
    alignItems: "center",
    marginRight: 8,
  },
});

export default Refunds;
